import {
  DEFAULT_REPLY_TEMPLATE,
  type MessageTemplate,
  preferredReplyTemplate,
  renderTemplate,
} from '@deizi/shared';
import { useState } from 'react';
import { Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal, Sheet } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { copyText } from '../../lib/messages';
import { useIsPhone } from '../../lib/use-media';
import { Combobox } from '../../components/Combobox';

/** wa.me link: with the customer's number when known (Brazil, +55), else just the text. */
export function whatsappUrl(text: string, phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  const to = digits.length >= 10 ? (digits.startsWith('55') ? digits : `55${digits}`) : '';
  return `https://wa.me/${to}?text=${encodeURIComponent(text)}`;
}

/**
 * "Resposta para o cliente": a message template filled with the quote values, editable before
 * copying (boards QQ-Desktop copy dialog, QQ-Celular-Resultado sheet).
 */
export function ReplyDialog({
  templates,
  values,
  whatsapp,
  marketplace = false,
  onClose,
  channelPicker,
}: {
  templates: MessageTemplate[];
  /** Placeholder values (from `quoteReplyValues`). */
  values: Record<string, string>;
  whatsapp?: string | null;
  /** Marketplace channel (has a commission): starts from the marketplace template. */
  marketplace?: boolean;
  onClose: () => void;
  /** Optional channel select (Quick Quote before saving). */
  channelPicker?: React.ReactNode;
}) {
  const phone = useIsPhone();
  const toast = useToast();
  const replies = templates.filter((t) => /orçamento/i.test(t.name));
  const list = replies.length ? replies : templates;
  const preferred = preferredReplyTemplate(templates, marketplace);
  const [templateId, setTemplateId] = useState<string | null>(preferred?.id ?? null);
  const template = list.find((t) => t.id === templateId);
  const auto = renderTemplate(template?.body ?? DEFAULT_REPLY_TEMPLATE, values);
  const [edited, setEdited] = useState<string | null>(null);
  const text = edited ?? auto;

  const copy = async () => {
    const ok = await copyText(text);
    toast(ok ? 'Copiado. É só colar.' : 'Não deu para copiar. Selecione o texto e copie.');
    if (ok) onClose();
  };

  const body = (
    <>
      {channelPicker}
      {list.length > 1 ? (
        <Field id="tpl" label="Modelo">
          <Combobox
            id="tpl"
            value={templateId ?? ''}
            options={list.map((t) => ({ value: t.id, label: t.name }))}
            onChange={(v) => {
              setTemplateId(v);
              setEdited(null);
            }}
          />
        </Field>
      ) : null}
      <Field
        id="msg"
        label={template ? `Mensagem · ${template.name}` : 'Mensagem'}
        help="Pode editar antes de copiar. O texto original fica no modelo."
      >
        <textarea
          id="msg"
          className="input reply"
          rows={6}
          style={{ minHeight: 150 }}
          value={text}
          onChange={(e) => setEdited(e.target.value)}
        />
      </Field>
    </>
  );

  if (phone) {
    return (
      <Sheet label="Resposta para o cliente" onClose={onClose}>
        <h2>Resposta para o cliente</h2>
        {body}
        <a
          className="btn p block"
          href={whatsappUrl(text, whatsapp)}
          target="_blank"
          rel="noreferrer"
        >
          <Icon name="phone" size={18} />
          Abrir no WhatsApp
        </a>
        <button type="button" className="btn block" onClick={() => void copy()}>
          <Icon name="copy" size={18} />
          Copiar
        </button>
      </Sheet>
    );
  }
  return (
    <Modal
      title="Resposta para o cliente"
      onClose={onClose}
      footer={
        <>
          {edited !== null ? (
            <button type="button" className="btn g" onClick={() => setEdited(null)}>
              Restaurar modelo
            </button>
          ) : null}
          <a className="btn" href={whatsappUrl(text, whatsapp)} target="_blank" rel="noreferrer">
            Abrir no WhatsApp
          </a>
          <button type="button" className="btn p" onClick={() => void copy()}>
            <Icon name="copy" size={18} />
            Copiar
          </button>
        </>
      }
    >
      {body}
    </Modal>
  );
}
